import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import Icon1 from "@assets/icons/Icon";
import { useState } from "react";
import { Project } from "types";
import { projectsApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { useAuthStore } from "@store/auth";

export const EditClientInfosModal = ({
  onClose,
  project,
  setProject,
  contactIndex,
}: {
  onClose: () => void;
  project: Project;
  setProject: (project: Project) => void;
  contactIndex: number;
}) => {
  const contact = project.infos.client.contacts[contactIndex];

  const [email, setEmail] = useState(contact?.email || "");
  const [phoneNumber, setPhoneNumber] = useState(contact?.phone_number || "");
  const [homeAddress, setHomeAddress] = useState(project.infos.general.home_address || "");
  const [homePostal, setHomePostal] = useState(project.infos.general.home_postal || "");
  const [homeCity, setHomeCity] = useState(project.infos.general.home_city || "");

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const updatedContacts = project.infos.client.contacts.map((c, i) =>
      i === contactIndex ? { ...c, email, phone_number: phoneNumber } : c,
    );
    const updatedProject = {
      ...project,
      infos: {
        ...project.infos,
        general: {
          ...project.infos.general,
          home_address: homeAddress,
          home_postal: homePostal,
          home_city: homeCity,
        },
        client: { ...project.infos.client, contacts: updatedContacts },
      },
    };
    // Changing the email of a contact that is linked to an account transfers
    // that account's access to the new email. Since this contact is the
    // client's own, they lose access to this project once it completes.
    const emailChanged =
      email.trim().toLowerCase() !==
      (contact.email || "").trim().toLowerCase();
    const willTransfer = emailChanged && !!contact.user_id;
    setIsSaving(true);
    try {
      // Persist phone/address (and the new email) first — while the client
      // still has access — then transfer the account.
      await projectsApi.update(project.id, { infos: updatedProject.infos });
      if (willTransfer) {
        await projectsApi.transferContactEmail(
          project.id,
          contactIndex,
          email.trim(),
        );
        showNotification({
          type: "success",
          message:
            "Votre adresse a été mise à jour. Une invitation a été envoyée à votre nouvelle adresse pour vous reconnecter.",
        });
        onClose();
        await useAuthStore.getState().logout();
        return;
      }
      setProject(updatedProject);
      showNotification({ type: "success", message: "Informations mises à jour" });
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!contact) return null;

  return (
    <BaseModal
      title="Modifier mes informations"
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="text-h3 font-medium text-neutral-500">Contact</div>
        <TextField
          label="Email"
          value={email}
          onChange={setEmail}
          placeholder="email@example.com"
        />
        <p className="text-xs text-neutral-400 px-1 -mt-1">
          Modifier cet email transfère votre accès : une invitation sera
          envoyée à la nouvelle adresse et vous devrez vous reconnecter avec
          celle-ci.
        </p>
        <TextField
          label="Téléphone"
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder="06 12 34 56 78"
          type="phone"
        />
        <div className="border-t border-neutral-200 my-1" />
        <div className="text-h3 font-medium text-neutral-500">Adresse du domicile</div>
        <TextField
          label="Adresse"
          value={homeAddress}
          onChange={setHomeAddress}
          placeholder="1 rue des fleurs"
        />
        <TextField
          label="Code postal"
          value={homePostal}
          onChange={setHomePostal}
          placeholder="35000"
        />
        <TextField
          label="Ville"
          value={homeCity}
          onChange={setHomeCity}
          placeholder="Rennes"
        />
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label={isSaving ? "Enregistrement..." : "Enregistrer"} onClick={handleSave} disabled={isSaving} />
      </div>
    </BaseModal>
  );
};
